// ── Ocean gray primitive ramp ───────────────────────────────────────────────
// bg/text 중립 semantic 토큰의 유일한 원료 — 정적 보더 5단은 무채 alpha 오버레이(borderInk)가,
// 상호작용 외곽 4단은 ocean 유채 축이 소유한다. light/dark가 같은 램프를 반대 방향으로
// 참조해 두 테마의 색온도가 일치한다 — 그 "일치"를 문장이 아니라 규칙으로 못 박은 게 이
// 램프다. primitive는 CSS 변수로 노출하지 않는다 — 컴포넌트는 semantic 토큰만 소비
// (RFC-semantic-token-map §토큰 네이밍 목표).
// 두 축은 채도비로 정의한다(OKLCH, 같은 밝기 ocean 단의 채도를 100%로 봤을 때):
//   면(라이트 gray10~60 · 다크 gray145~210) — 45%, h≈257 네이비. 두 테마가 같은 규칙이다
//     (다크 near-black 3단 gray190~210만 8비트 양자화 한계로 25~40%에 머문다).
//   잉크(gray80~136) — 24%, h≈242. 면보다 옅은 유채라 텍스트가 면과 같은 색으로 읽히지 않는다.
//     이 축은 텍스트 전용이다 — 보더에 쓰면 정적 단과 채도가 겹친다.
// 유채(ocean 램프)는 채도 100% · h222~230이므로 면과는 채도비 2.2배 + hue 30°, 두 겹으로
// 갈린다 — 면에서 hue를 지우는 대신 채도 간격으로 가른다. 라이트 면을 무채색으로 눌러 두면
// 두 테마의 색온도가 갈리고, 다크 본문이 자기 muted 텍스트보다 무채색이 되는 역전이 생긴다.
// 다크 스텝은 시트 앵커(#04060B canvas · #0D121C surface) 위로 raised 이상이 뚜렷이 밝아지는
// 확장 사다리 — 배경과 surface 가 구분돼야 한다.
// 재조색의 불변량은 밝기(L*)다 — L*는 상대휘도와 일대일이라 밝기를 보존하면 기존 대비쌍이
// 그대로 유지된다(사다리 단조성과 대비 하한은 cssVariables.test.ts가 가드).
const oceanGray = {
  white: "#ffffff",
  gray10: "#f9fafb",
  gray20: "#eff2f7",
  gray30: "#e6ecf3",
  gray40: "#d6dfeb",
  gray50: "#c6d1e2",
  gray60: "#b8c7dc", // 라이트 border.disabled · slider disabled
  // gray80: 시트 bridge 원색 파생 — 다크 text.muted(sub-text) 전용 슬레이트 스텝. gray90과
  // 함께 잉크 축(24% · h≈242)의 상단이다.
  gray80: "#9cabb7",
  gray90: "#90a2af",
  gray110: "#788b9b", // 라이트 slider.tick.enabled
  gray130: "#4e616f", // 다크 border.disabled · slider disabled
  // gray133·gray136: 라이트 text.subtle/muted 전용 슬레이트 보간 스텝 — 가장 진한 라이트
  // surface(surfaceOverlay #c6d1e2) 위에서도 4.5:1을 넘는 단으로 두어 어느 surface에나
  // 얹을 수 있다(gray136 5.30:1 / gray133 4.76:1 on surfaceOverlay, 8.17:1 / 7.33:1 on
  // white).
  gray133: "#485866",
  gray136: "#42515e",
  // gray145~160: 다크 확장 사다리 스텝 — surface(gray170) 위로 raised(160) <
  // raisedHigh(155) < overlay(150) < neutralActive(145)가 단차 1.13~1.19로 밝아진다.
  gray145: "#31405a",
  gray150: "#27354a",
  gray155: "#1e2a3c",
  gray160: "#16202e",
  gray170: "#0d121c",
  gray180: "#0b0f18",
  // gray190: 라이트 bg.muted alpha base 전용(다크 bg 스텝에서는 제외).
  gray190: "#090c14",
  gray200: "#04060b",
  gray210: "#030407",
} as const;

// ── Coastal ocean ramp ──────────────────────────────────────────────────────
// 유채색(액션·컨테이너·accent·상호작용 외곽) semantic 토큰의 유일한 원료 — 한 테이블에서
// 라이트는 어두운 구간을, 다크는 밝은 구간을 가져다 쓴다.
// 보더 8단 중 정적 5단은 이 램프를 쓰지 않고(무채 alpha 오버레이), 상호작용 4단
// (focus/selected*)이 이 램프를 쓴다. 두 축은 채도로 갈린다 — 합성된 정적 단은 최대 19%,
// 상호작용 축은 69~100%다. 액션 fill과는 소비처(면 vs 외곽)와 밝기 단으로 갈린다.
// 세 축이 모두 단조다:
//   밝기 — oceanGray와 같은 좌표계라 oceanN은 grayN과 같은 밝기다. 인접 단 ΔL* 3.3~5.3.
//          양 끝(10~30 · 170~210)은 L* 축에 남은 범위 자체가 좁아 2.6 · 1.1로 촘촘한데,
//          oceanGray 램프도 같은 리듬이다.
//   색상 — OKLCH h220(밝은 끝) → h266(어두운 끝) 단조 드리프트. 인접 단 최대 3°.
//   채도 — OKLCH C 0.006 → 0.140(L*48 정점) → 0.042 단봉 곡선. 전 단 sRGB 게이멋 내
//          (near-black은 8비트 양자화 한계라 게이멋 클램프 대상에서 제외).
// primitive는 CSS 변수로 노출하지 않는다.
const ocean = {
  ocean10: "#f5fafc",
  ocean20: "#e7f5f9",
  ocean30: "#d9eff7", // 라이트 컨테이너 rest(primarySubtle · secondary)
  ocean40: "#c1e4f2", // 라이트 컨테이너 hover
  ocean50: "#a8d8ed", // 라이트 컨테이너 active
  ocean60: "#91ceec", // 라이트 secondary rest
  // ocean65: 라이트 secondary hover 전용 신설 half-step — ocean60/70 사이 곡선 위 보간값.
  ocean65: "#7fc7eb",
  ocean70: "#70c1ea", // 라이트 secondary active
  ocean80: "#4db3e7", // Light action rest
  ocean90: "#40a9e0", // Light action hover · Dark text accent
  ocean100: "#309cd9", // Light action active
  // ocean110: selected alpha base(양 테마)
  ocean110: "#1d90d1",
  ocean115: "#0f84c8",
  ocean120: "#0078be", // Dark action active
  ocean125: "#006cb2", // Dark action hover
  ocean130: "#0061a6", // Dark action rest
  ocean135: "#0c5493", // 라이트 text.accent — surfaceOverlay 위 5.03:1(AA)
  // ocean137: 다크 secondary active 전용 신설 half-step — ocean135/140 사이 곡선 위 보간값.
  ocean137: "#0e4f8d",
  // ocean140: slider pressed와 raw를 공유한다(한 raw 값의 다중 intent 선례).
  ocean140: "#0f4984", // 다크 secondary hover
  ocean145: "#124077", // 다크 secondary rest
  ocean150: "#123464", // 다크 컨테이너 active
  ocean155: "#112852", // Light onControl · 다크 컨테이너 hover
  ocean160: "#0e1f41", // Dark container rest
  ocean165: "#0b1736",
  // ocean170~210: 다크 near-black surface 단(surface·canvas·sunken)에 대응하는 틴트.
  ocean170: "#07102a",
  ocean180: "#060d26",
  ocean190: "#050b21",
  ocean200: "#03071a",
  ocean210: "#010313",
} as const;

// ── Classic Red reserve ramp ────────────────────────────────────────────────
// danger semantic 토큰의 유일한 원료 — red 계열은 UI 테마가 아니라 error 시맨틱 예비
// (시트 §Semantic Reserve · Red)다. 번호는 밝기 순서이고 전 단이 한 계열(h≈25)이다 —
// warning은 hue가 갈려야 구분되므로 별도 orange 램프가 소유한다. fill은 두 테마가 램프를
// 반대 방향으로 참조한다 — 라이트는 red50에서 시작해 채도 정점(red72, L* 63.0 · C 0.256)까지
// 어두워지고, 다크는 red88에서 시작해 red77까지 밝아진다. primary와 같은 극성이라 전경도 같은 방향
// (라이트 near-black · 다크 white)을 쓴다. 텍스트·보더 축은 fill과 분리된 채로 남는다.
// 사다리는 5~150 전 구간 밝기 단조(L* 93.7→16.1)이며 전 단이 fill 곡선(h≈25, 채도
// 단봉 — 정점 red72 C 0.256) 위에 있다. 시맨틱 슬롯이 없는 구간은 같은 곡선 위 보간
// 단이 잇는다.
const red = {
  red5: "#fbe9e9", // 시트 warning tint 원색 — 라이트 알림 면(bg.dangerSurface) 단
  red10: "#ffd1d0", // 보간 단
  red20: "#ffb4b1", // 보간 단
  red30: "#ff928c", // 보간 단
  red35: "#ff6e68", // 다크 text.danger — 가장 밝은 면(surfaceOverlay) 위 4.53:1
  red50: "#ff5c57", // 라이트 danger fill rest · status.recording(브랜드 레드 #ff4b4b 근접 단 — 다크 error와 ΔE 8.3 분리). near-black 잉크 6.32:1
  red70: "#ff4042", // 라이트 danger fill hover(5.53:1) · 다크 border.danger(overlay 위 3.58:1)
  red72: "#ff0028", // 정점 단 — 라이트 danger fill active, near-black 잉크 4.82:1
  red75: "#f40026", // 양 테마 border.dangerSurface — dangerSurface 면 위 3.68:1(라이트) · 3.05:1(다크)
  red77: "#ed0025", // 다크 danger fill active — white 4.54:1
  red80: "#e30023", // 라이트 border.danger — 모든 라이트 surface(overlay 포함) 위 3.17:1 이상
  red85: "#d80020", // 다크 danger fill hover — white 5.33:1
  red88: "#c3001c", // 다크 danger fill rest — white 6.29:1
  red100: "#b40f1c", // 보간 단
  red120: "#a4161a", // 시트 error text — 라이트 text.danger · text.onDangerSurface
  red130: "#840e11", // 보간 단
  red140: "#660708", // 시트 error surface — 다크 알림 면(bg.dangerSurface) 단
  red150: "#550807", // 보간 단 — 사다리 바닥
} as const;

// ── Orange reserve ramp ─────────────────────────────────────────────────────
// warning semantic 토큰의 유일한 원료. red에서 warning을 떼어낸 이유는 hue다 — 한 램프
// 안에서 밝기로만 가르면 danger와 warning이 같은 색으로 읽힌다. 주황은 h≈53으로 두어
// 빨강(h≈25)과 28° 벌린다.
// 전 단이 sRGB 가장자리라 같은 밝기에서 채도를 더 올릴 수 없다 — 그래서 fill은 밝기를
// 올려 채도 정점(orange40, L* 72.9 · C 0.186)에 다가가는 방식으로만 쨍해진다. 라이트는
// orange20에서 시작해 정점으로 어두워지고, 다크는 orange60에서 시작해 같은 정점으로
// 밝아진다 — 두 테마가 orange40에서 만난다. orange150은 라이트 text 전용 단이다.
// 사다리는 5~150 전 구간 밝기 단조(L* 95.7→37.8)이며 h≈53 한 축 위에 있다 — 시맨틱
// 슬롯이 없는 구간은 같은 곡선 위 보간 단이 잇는다.
// primitive는 CSS 변수로 노출하지 않는다.
const orange = {
  orange5: "#fff0e7", // 라이트 알림/틴트 면 후보 단
  orange10: "#ffc9a7", // 보간 단
  orange20: "#ffa56a", // 라이트 warning fill rest — near-black 잉크 9.89:1
  orange30: "#ff9346", // 라이트 warning hover — 8.68:1
  // 정점 단 — 라이트 active·다크 active·다크 text.warning이 함께 쓴다.
  orange40: "#fe7f00", // 라이트 warning active(7.53:1) · 다크 warning active(white 2.55:1) · 다크 text.warning
  orange50: "#ee7700", // 다크 warning fill hover — white 2.89:1
  orange60: "#de6f00", // 다크 warning fill rest — white 3.29:1
  orange70: "#cf6702", // 보간 단
  orange90: "#bd5d01", // 보간 단
  orange110: "#ad5400", // 보간 단
  orange130: "#9e4c00", // 보간 단
  orange150: "#8f4400", // 라이트 text.warning — surfaceOverlay 위 4.55:1
} as const;

// ── Green reserve ramp ──────────────────────────────────────────────────────
// success/ready 시맨틱 예비(시트 §Semantic Reserve와 같은 성격의 status green 계열).
// 전 단이 하나의 OKLCH 곡선 위에 있다 — 밝기 L* 96.4→36.5 단조(인접 단 ΔL* 3.6~6.6),
// hue 151→147 단조 드리프트, 채도는 C 0.030→0.212(green40 정점)→0.118 단봉 곡선.
// danger(h≈25)·warning(h≈53)과는 hue로, ocean(h≈222~266)과는 반대 반구로 갈린다(최소
// 간격 91°). 제품 전반의 green 리터럴은 값 그대로가 아니라 곡선 위 최근접 단으로
// 스냅된다 — 어느 리터럴이 어느 단으로 오는지는 각 단 주석이 기록한다.
// primitive는 CSS 변수로 노출하지 않는다.
const green = {
  green5: "#e6fae9", // 라이트 알림/틴트 면 후보 단 — red5와 같은 자리
  green10: "#bff1c8",
  green20: "#91e7a4",
  green30: "#59da7c",
  green40: "#00ce5a", // 정점 단(C 0.212) — status.success 다크(#00cf4c) 스냅 단. near-black 잉크 9.10:1
  green50: "#0bba4f", // 라이트 fill 후보 — status.success 라이트(#09b649)·sigmakit on/LED(#22c55e) 스냅 단. near-black 잉크 7.44:1
  green60: "#1ead4a", // status.ready(#4caf50) 스냅 단. near-black 잉크 6.51:1
  green70: "#22a247", // train SUCCEEDED 합의값(#16a34a) 스냅 단 — job.succeeded가 success(green50)로 확정되어 현재 미참조 예비 단. white 3.32:1
  green90: "#21913e",
  green110: "#1b8134",
  green130: "#18722d",
  green150: "#166328", // 라이트 text green 후보 단 — white 7.37:1
} as const;

// ── Amber ramp ──────────────────────────────────────────────────────────────
// paused/pending/attention 시맨틱의 원료 — yellow 친화 축이다. hue 90→75 단조 드리프트로
// 전 단이 orange(h≈53)와 22° 이상 벌어져, 한 화면에서 warning과 나란히 앉아도 갈린다.
// 밝기 L* 95.9→34.6 단조(인접 단 ΔL* 3.8~8.1), 채도는 C 0.034→0.164(amber30 정점)→0.090
// 단봉 곡선. status.paused는 두 테마 모두 채도 정점인 amber30을 사용하고, 사용자 조치가
// 필요한 status.attention은 더 어두운 amber50을 사용한다. primitive는 CSS 변수로 노출하지 않는다.
const amber = {
  amber5: "#fcf3da", // 라이트 알림/틴트 면 후보 단
  amber10: "#f8e1a6",
  amber20: "#f7ce6d",
  amber30: "#fbbf24", // 정점 단 — status.paused 양 테마 공통 단. near-black 잉크 11.48:1
  amber40: "#efb000",
  amber50: "#e0a400", // status.attention 양 테마 공통 단. near-black 잉크 8.65:1
  amber60: "#d29700",
  amber70: "#c18a00", // white 3.04:1
  amber90: "#a97500",
  amber110: "#8f6203",
  amber130: "#7f5605",
  amber150: "#6f4a06", // 라이트 text amber 후보 단 — white 7.90:1
} as const;

// ── Blue reserve ramp ───────────────────────────────────────────────────────
// status blue 계열(info·completed 등)의 원료. ocean과는 같은 밝기에서 hue +15~19° ·
// 채도 1.4~1.6배, 두 겹으로 갈린다(ocean 축은 h222~251 · C≤0.14, 이 축은 h254~266 ·
// C 0.16~0.22) — oceanGray/ocean을 채도비로 가른 것과 같은 규칙이다. 사다리는 5~150
// 전 구간 밝기 단조(L* 95.9→30.0), hue 254→266 단조 드리프트, 채도는 단봉 곡선
// (정점 blue90 C 0.215). 앵커 세 단(blue60/70/90)은 제품 리터럴 값 그대로다.
// primitive는 CSS 변수로 노출하지 않는다.
const blue = {
  blue5: "#ecf4ff", // 라이트 알림/틴트 면 후보 단
  blue10: "#d1e5ff",
  blue20: "#b3d4ff",
  blue30: "#94c0ff",
  blue40: "#74acff",
  blue50: "#5396ff",
  blue60: "#3b82f6", // 앵커 — status.info 원색
  blue70: "#2773ee", // 앵커 — status.completed 원색
  blue90: "#2563eb", // 정점 단(C 0.215) — sigmakit·hatchery train RUNNING 합의 원색
  blue110: "#2053d9", // white 6.36:1
  blue130: "#2047bd", // white 7.78:1
  blue150: "#203e9e", // 라이트 text blue 후보 단 — white 9.36:1
} as const;

// ── Violet reserve ramp ─────────────────────────────────────────────────────
// 보라 계열 시맨틱 예비 — hatchery-registry의 exhausted status 확장 토큰 등 제품의
// 보라 리터럴을 수용하는 축이다. blue(h254~266)와 hue 27° 이상 벌어진다. 사다리는
// 5~150 전 구간 밝기 단조(L* 95.9→29.7), hue 302(밝은 끝)→293(어두운 끝) 단조
// 드리프트, 채도는 단봉 곡선(정점 violet90 C 0.247). 앵커 세 단(violet40/90/130)은
// 제품 리터럴 값 그대로다. primitive는 CSS 변수로 노출하지 않는다.
const violet = {
  violet5: "#f6f1ff", // 라이트 알림/틴트 면 후보 단
  violet10: "#e9dcff",
  violet20: "#dbc6ff",
  violet30: "#cdadff",
  violet40: "#c297ff", // 앵커 — registry exhausted status 다크 원색
  violet50: "#b081fa",
  violet60: "#9f69f7",
  violet70: "#8e52f5",
  violet90: "#7c3aed", // 정점 단(C 0.247) — 앵커, 제품 배지 보라 원색
  violet110: "#703ad0",
  violet130: "#6639ba", // 앵커 — registry exhausted status 라이트 원색
  violet150: "#58339f", // 라이트 text violet 후보 단
} as const;

// 램프 스텝(hex) → "rgb(R G B / a)" — 알파 state layer(muted/selected/disabled)의 base를
// 프리미티브 스텝에 고정해 파생 관계를 코드로 남긴다(RFC-semantic-token-map §solid vs alpha 허용군).
const alpha = (hex: string, a: number): string => {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `rgb(${r} ${g} ${b} / ${a})`;
};

// 정적 보더 5단(divider~emphasis)의 base. 램프 스텝이 아니라 면 위에 얹는 무채 오버레이라
// 어느 램프에도 속하지 않는다 — attentionAmber와 같은 이유로 여기 한 번만 적고 열 개 값을
// alpha()로 파생한다. 이 축을 alpha로 두는 근거는 아래 border 절.
const borderInk = { light: "#000000", dark: "#ffffff" } as const;

// attention state layer의 amber base. 여기서 한 번만 적고 세 단계(rest/hover/active)를
// alpha()로 파생한다 — 리터럴로 흩어 두면 재조색 때 muted/selected만 갱신되고 attention이
// 조용히 빠진다. dark는 amber30과 같은 값이지만 light(#f59e0b, h 70)는 amber 램프의
// yellow 축(h 75~90) 밖 orange 쪽 값이라, 쌍 전체를 램프에 속하지 않는 독립 축으로 둔다.
const attentionAmber = { light: "#f59e0b", dark: "#fbbf24" } as const;

// 회사 브랜드 레드 — rgb(255,75,75). 아이덴티티 축이라 램프에 스냅하지 않는 독립 base다
// (스냅하면 브랜드 색이 재조색을 따라 흔들린다). status.error(red70 #ff4042)와 ΔE 1.5로
// 근접하지만 intent가 다르다 — 브랜드 표기는 이 축, 오류 신호는 status/danger 축.
const brandRed = "#ff4b4b" as const;

export {
  alpha,
  amber,
  attentionAmber,
  brandRed,
  blue,
  borderInk,
  green,
  ocean,
  oceanGray,
  orange,
  red,
  violet,
};
